import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../api/config';

const AdminSimple = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isCheckingSession, setIsCheckingSession] = useState(true);
  const [credentials, setCredentials] = useState({
    email: '',
    password: ''
  });
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentView, setCurrentView] = useState('users'); // 'users' or 'contacts'
  const [error, setError] = useState('');

  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: '#f5f5f5',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    loginBox: {
      maxWidth: '400px',
      width: '90%',
      padding: '40px',
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    },
    title: {
      fontSize: '28px',
      fontWeight: 'bold',
      marginBottom: '30px',
      textAlign: 'center',
      color: '#333',
    },
    formGroup: {
      marginBottom: '20px',
    },
    label: {
      display: 'block',
      marginBottom: '8px',
      fontSize: '14px',
      fontWeight: '500',
      color: '#555',
    },
    input: {
      width: '100%',
      padding: '12px',
      fontSize: '16px',
      border: '1px solid #ddd',
      borderRadius: '6px',
      boxSizing: 'border-box',
    },
    button: {
      width: '100%',
      padding: '12px',
      fontSize: '16px',
      fontWeight: '600',
      color: 'white',
      backgroundColor: '#3b82f6',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
    },
    buttonHover: {
      backgroundColor: '#2563eb',
    },
    error: {
      color: '#ef4444',
      fontSize: '14px',
      marginBottom: '16px',
      padding: '10px',
      backgroundColor: '#fee',
      borderRadius: '4px',
    },
    dashboard: {
      padding: '20px',
      backgroundColor: '#f5f5f5',
      minHeight: '100vh',
    },
    header: {
      backgroundColor: 'white',
      padding: '20px',
      marginBottom: '20px',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '20px',
      marginBottom: '30px',
    },
    statCard: {
      backgroundColor: 'white',
      padding: '20px',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    },
    statLabel: {
      fontSize: '14px',
      color: '#666',
      marginBottom: '8px',
    },
    statValue: {
      fontSize: '32px',
      fontWeight: 'bold',
      color: '#333',
    },
    table: {
      width: '100%',
      backgroundColor: 'white',
      borderRadius: '8px',
      overflow: 'hidden',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    },
    tableHeader: {
      backgroundColor: '#f8f9fa',
      padding: '16px',
      borderBottom: '1px solid #e5e7eb',
    },
    tableTitle: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#333',
    },
    tableBody: {
      width: '100%',
      borderCollapse: 'collapse',
    },
    th: {
      padding: '12px',
      textAlign: 'left',
      borderBottom: '1px solid #e5e7eb',
      backgroundColor: '#f8f9fa',
      fontWeight: '600',
      fontSize: '14px',
      color: '#666',
    },
    td: {
      padding: '12px',
      borderBottom: '1px solid #e5e7eb',
      fontSize: '14px',
      color: '#333',
    },
    pagination: {
      padding: '16px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      borderTop: '1px solid #e5e7eb',
    },
    paginationButton: {
      padding: '8px 16px',
      backgroundColor: '#3b82f6',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '14px',
    },
    deleteButton: {
      padding: '6px 12px',
      backgroundColor: '#ef4444',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '12px',
    },
    logoutButton: {
      padding: '10px 20px',
      backgroundColor: '#ef4444',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '600',
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('adminToken');

    if (!token) {
      setIsCheckingSession(false);
      return;
    }

    loadDashboard().then((ok) => {
      if (ok) {
        setIsLoggedIn(true);
      }
    }).finally(() => {
      setIsCheckingSession(false);
    });
  }, []);

  const clearAdminSession = () => {
    localStorage.removeItem('adminToken');
    setIsLoggedIn(false);
    setStats(null);
    setUsers([]);
    setContacts([]);
    setCurrentPage(1);
  };

  const handleAuthFailure = () => {
    clearAdminSession();
    setError('Session expired. Please sign in again.');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('adminToken', data.token);
        const ok = await loadDashboard();

        if (ok) {
          setIsLoggedIn(true);
        }
      } else {
        setError(data.error || 'Login failed');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    }
  };

  const handleLogout = () => {
    clearAdminSession();
    setError('');
  };

  const loadDashboard = async () => {
    const statsOk = await loadStats();
    if (!statsOk) return false;

    const usersOk = await loadUsers(1);
    if (!usersOk) return false;

    return loadContacts();
  };

  const isAuthError = (response) => response.status === 401 || response.status === 403;

  const loadStats = async () => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${API_BASE_URL}/admin/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data.stats);
        return true;
      }

      if (isAuthError(response)) {
        handleAuthFailure();
        return false;
      }

      return false;
    } catch (err) {
      console.error('Error loading stats:', err);
      return false;
    }
  };

  const loadUsers = async (page) => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${API_BASE_URL}/admin/users?page=${page}&limit=100`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users);
        setCurrentPage(page);
        return true;
      }

      if (isAuthError(response)) {
        handleAuthFailure();
        return false;
      }

      return false;
    } catch (err) {
      console.error('Error loading users:', err);
      return false;
    }
  };

  const loadContacts = async () => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${API_BASE_URL}/admin/contacts`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setContacts(data.contacts);
        return true;
      }

      if (isAuthError(response)) {
        handleAuthFailure();
        return false;
      }

      return false;
    } catch (err) {
      console.error('Error loading contacts:', err);
      return false;
    }
  };

  const deleteUser = async (id) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    const token = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${API_BASE_URL}/admin/users/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        await loadUsers(currentPage);
        await loadStats();
      } else if (isAuthError(response)) {
        handleAuthFailure();
      }
    } catch (err) {
      console.error('Error deleting user:', err);
    }
  };

  if (isCheckingSession) {
    return (
      <div style={styles.container}>
        <div style={styles.loginBox}>
          <h2 style={styles.title}>Checking session...</h2>
        </div>
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <div style={styles.container}>
        <div style={styles.loginBox}>
          <h2 style={styles.title}>🔐 Admin Portal</h2>
          <form onSubmit={handleLogin}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Email Address</label>
              <input
                type="email"
                value={credentials.email}
                onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
                style={styles.input}
                placeholder="admin@example.com"
                required
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Password</label>
              <input
                type="password"
                value={credentials.password}
                onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                style={styles.input}
                placeholder="Enter your password"
                required
              />
            </div>
            {error && (
              <div style={styles.error}>{error}</div>
            )}
            <button 
              type="submit" 
              style={styles.button}
              onMouseOver={(e) => e.target.style.backgroundColor = '#2563eb'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#3b82f6'}
            >
              Sign In
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.dashboard}>
      <div style={styles.header}>
        <div>
          <h1 style={{ margin: 0, fontSize: '24px', color: '#333' }}>Admin Dashboard</h1>
          <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#666' }}>Manage your application</p>
        </div>
        <button style={styles.logoutButton} onClick={handleLogout}>
          Logout
        </button>
      </div>

      {stats && (
        <div style={styles.statsGrid}>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Total Users</div>
            <div style={styles.statValue}>{stats.users}</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Paid Users</div>
            <div style={{ ...styles.statValue, color: '#10b981' }}>{stats.paid}</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Unpaid Users</div>
            <div style={{ ...styles.statValue, color: '#ef4444' }}>{stats.unpaid}</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Profiles</div>
            <div style={styles.statValue}>{stats.profiles}</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Posts</div>
            <div style={styles.statValue}>{stats.posts}</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Comments</div>
            <div style={styles.statValue}>{stats.comments}</div>
          </div>
        </div>
      )}

      {/* Navigation Tabs */}
      <div style={{ marginBottom: '20px', display: 'flex', gap: '10px' }}>
        <button 
          onClick={() => setCurrentView('users')}
          style={{
            ...styles.paginationButton,
            backgroundColor: currentView === 'users' ? '#3b82f6' : '#6b7280'
          }}
        >
          Users ({users.length})
        </button>
        <button 
          onClick={() => setCurrentView('contacts')}
          style={{
            ...styles.paginationButton,
            backgroundColor: currentView === 'contacts' ? '#3b82f6' : '#6b7280'
          }}
        >
          Contact Forms ({contacts.length})
        </button>
      </div>

      {/* Users Table */}
      {currentView === 'users' && (
        <div style={styles.table}>
        <div style={styles.tableHeader}>
          <h2 style={styles.tableTitle}>User Management</h2>
        </div>
        <table style={styles.tableBody}>
          <thead>
            <tr>
              <th style={styles.th}>ID</th>
              <th style={styles.th}>Date</th>
              <th style={styles.th}>Email</th>
              <th style={styles.th}>Full Name</th>
              <th style={styles.th}>Field</th>
              <th style={styles.th}>Phone</th>
              <th style={styles.th}>Salary</th>
              <th style={styles.th}>Paid</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id}>
                <td style={styles.td}>{user.id}</td>
                <td style={styles.td}>{new Date(user.created_at).toLocaleDateString()}</td>
                <td style={styles.td}>{user.email}</td>
                <td style={styles.td}>{user.full_name || '-'}</td>
                <td style={styles.td}>{user.field || '-'}</td>
                <td style={styles.td}>{user.phone_number || '-'}</td>
                <td style={styles.td}>{user.salary || '-'}</td>
                <td style={styles.td}>
                  <span style={{
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    backgroundColor: user.paid ? '#d4edda' : '#f8d7da',
                    color: user.paid ? '#155724' : '#721c24',
                  }}>
                    {user.paid ? 'Yes' : 'No'}
                  </span>
                </td>
                <td style={styles.td}>
                  <button style={styles.deleteButton} onClick={() => deleteUser(user.id)}>
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={styles.pagination}>
          <button 
            style={styles.paginationButton} 
            onClick={() => loadUsers(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <span>Page {currentPage}</span>
          <button 
            style={styles.paginationButton} 
            onClick={() => loadUsers(currentPage + 1)}
          >
            Next
          </button>
        </div>
        </div>
      )}

      {/* Contacts Table */}
      {currentView === 'contacts' && (
        <div style={styles.table}>
          <div style={styles.tableHeader}>
            <h2 style={styles.tableTitle}>Contact Form Submissions</h2>
          </div>
          <table style={styles.tableBody}>
            <thead>
              <tr>
                <th style={styles.th}>ID</th>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Email</th>
                <th style={styles.th}>Phone</th>
                <th style={styles.th}>Message</th>
                <th style={styles.th}>Submitted</th>
              </tr>
            </thead>
            <tbody>
              {contacts.map((contact) => (
                <tr key={contact.id}>
                  <td style={styles.td}>{contact.id}</td>
                  <td style={styles.td}>{contact.first_name} {contact.last_name}</td>
                  <td style={styles.td}>{contact.email}</td>
                  <td style={styles.td}>{contact.phone}</td>
                  <td style={{ ...styles.td, maxWidth: '300px', wordWrap: 'break-word' }}>
                    {contact.message}
                  </td>
                  <td style={styles.td}>
                    {new Date(contact.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={styles.pagination}>
            <span>Total: {contacts.length} submissions</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSimple;
